import { MD3DarkTheme, MD3LightTheme, configureFonts } from 'react-native-paper';

const fontConfig = {
  displayLarge: { fontFamily: 'System', fontWeight: '400' as const },
  displayMedium: { fontFamily: 'System', fontWeight: '400' as const },
  displaySmall: { fontFamily: 'System', fontWeight: '400' as const },
  headlineLarge: { fontFamily: 'System', fontWeight: '400' as const },
  headlineMedium: { fontFamily: 'System', fontWeight: '400' as const },
  headlineSmall: { fontFamily: 'System', fontWeight: '400' as const },
  titleLarge: { fontFamily: 'System', fontWeight: '500' as const },
  titleMedium: { fontFamily: 'System', fontWeight: '500' as const },
  titleSmall: { fontFamily: 'System', fontWeight: '500' as const },
  bodyLarge: { fontFamily: 'System', fontWeight: '400' as const },
  bodyMedium: { fontFamily: 'System', fontWeight: '400' as const },
  bodySmall: { fontFamily: 'System', fontWeight: '400' as const },
  labelLarge: { fontFamily: 'System', fontWeight: '500' as const },
  labelMedium: { fontFamily: 'System', fontWeight: '500' as const },
  labelSmall: { fontFamily: 'System', fontWeight: '500' as const },
};

// Semantic color tokens matching the HTML mockup
export interface AppColors {
  // Core backgrounds
  bg: string;
  bgHeader: string;
  card: string;
  // Text
  text: string;
  textSecondary: string;
  textTertiary: string;
  // Borders
  border: string;
  // Interactive
  accent: string;
  accentText: string;
  // Status
  success: string;
  warning: string;
  error: string;
  // Belt bar
  beltBarBg: string;
  // Misc
  qrBg: string;
  badgeBg: string;
  badgeText: string;
  // Tab bar
  tabActive: string;
  tabInactive: string;
  tabBarBg: string;
}

export const darkColors: AppColors = {
  bg: '#050505',
  bgHeader: '#0a0a0a',
  card: '#121212',
  text: '#ffffff',
  textSecondary: '#9ca3af',  // gray-400
  textTertiary: '#6b7280',   // gray-500
  border: '#1f2937',         // gray-800
  accent: '#ffffff',
  accentText: '#000000',
  success: '#22c55e',
  warning: '#f59e0b',
  error: '#ef4444',
  beltBarBg: '#111111',
  qrBg: '#ffffff',
  badgeBg: '#ffffff',
  badgeText: '#000000',
  tabActive: '#ffffff',
  tabInactive: '#6b7280',
  tabBarBg: '#050505',
};

export const lightColors: AppColors = {
  bg: '#f8f9fa',
  bgHeader: '#ffffff',
  card: '#ffffff',
  text: '#111827',
  textSecondary: '#6b7280',
  textTertiary: '#9ca3af',
  border: '#e5e7eb',
  accent: '#111827',
  accentText: '#ffffff',
  success: '#16a34a',
  warning: '#d97706',
  error: '#dc2626',
  beltBarBg: '#e5e7eb',
  qrBg: '#ffffff',
  badgeBg: '#111827',
  badgeText: '#ffffff',
  tabActive: '#111827',
  tabInactive: '#9ca3af',
  tabBarBg: '#ffffff',
};

export const darkTheme = {
  ...MD3DarkTheme,
  fonts: configureFonts({ config: fontConfig }),
  colors: {
    ...MD3DarkTheme.colors,
    primary: '#ffffff',
    primaryContainer: '#1f2937',
    secondary: '#9ca3af',
    secondaryContainer: '#1f2937',
    tertiary: '#6b7280',
    tertiaryContainer: '#111111',
    surface: '#121212',
    surfaceVariant: '#1f2937',
    background: '#050505',
    error: '#ef4444',
    onPrimary: '#000000',
    onSecondary: '#ffffff',
    onTertiary: '#ffffff',
    onSurface: '#ffffff',
    onBackground: '#ffffff',
    outline: '#374151',
  },
};

export const lightTheme = {
  ...MD3LightTheme,
  fonts: configureFonts({ config: fontConfig }),
  colors: {
    ...MD3LightTheme.colors,
    primary: '#111827',
    primaryContainer: '#e5e7eb',
    secondary: '#6b7280',
    secondaryContainer: '#f3f4f6',
    tertiary: '#9ca3af',
    tertiaryContainer: '#e5e7eb',
    surface: '#ffffff',
    surfaceVariant: '#f3f4f6',
    background: '#f8f9fa',
    error: '#dc2626',
    onPrimary: '#ffffff',
    onSecondary: '#111827',
    onTertiary: '#111827',
    onSurface: '#111827',
    onBackground: '#111827',
    outline: '#d1d5db',
  },
};

export const beltColors: Record<string, string> = {
  white: '#f5f5f5',
  gray: '#6b7280',
  yellow: '#eab308',
  orange: '#f97316',
  green: '#22c55e',
  blue: '#1565c0',
  purple: '#7b1fa2',
  brown: '#6d4c41',
  black: '#212121',
};

export const beltGradients: Record<string, [string, string, string]> = {
  white: ['#e5e7eb', '#f5f5f5', '#e5e7eb'],
  gray: ['#4b5563', '#6b7280', '#4b5563'],
  yellow: ['#ca8a04', '#eab308', '#ca8a04'],
  orange: ['#ea580c', '#f97316', '#ea580c'],
  green: ['#16a34a', '#22c55e', '#16a34a'],
  blue: ['#1e40af', '#2563eb', '#1e40af'],
  purple: ['#7e22ce', '#9333ea', '#7e22ce'],
  brown: ['#78350f', '#92400e', '#78350f'],
  black: ['#1f2937', '#374151', '#1f2937'],
};
